import { useEffect, useRef, useState } from 'react';
import { Portrait, HUMAN_CHARACTER_KEYS } from '../components/Portrait.tsx';
import { useI18n } from '../i18n/index.tsx';
import { randomAvatar } from '@rummy/protocol';
import { authApi, mountGoogleButton, type AuthError } from '../net/auth.ts';
import type { ConnectionState } from '../net/client.ts';

export interface AuthProps {
  initialHandle: string;
  initialAvatar: string;
  /** Profile complete (guest or fresh registration) — enter the club. */
  onSubmit: (handle: string, avatar: string) => void;
  /** Credentials accepted. The app re-identifies the socket; when `fresh` is
   *  false there is no profile left to collect and onboarding ends here. */
  onSignedIn: (fresh: boolean) => void;
  /** The transport, not a summary of it — "opening" and "cannot reach the
   *  server" are different situations and this screen has to say which. */
  connection: ConnectionState;
  /** True once the player has committed and we are waiting on the server. */
  pending: boolean;
  onRetry: () => void;
}

type Step = 'welcome' | 'profile';
type Mode = 'signin' | 'signup';

/** §6.1 screen 1 — the club door: three ways in, none of them a form maze. */
export function Auth({
  initialHandle,
  initialAvatar,
  onSubmit,
  onSignedIn,
  connection,
  pending,
  onRetry,
}: AuthProps) {
  const { t } = useI18n();
  const [step, setStep] = useState<Step>('welcome');
  const [mode, setMode] = useState<Mode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  const [handle, setHandle] = useState(initialHandle === 'Guest' ? '' : initialHandle);
  const [avatar, setAvatar] = useState(initialAvatar || HUMAN_CHARACTER_KEYS[0]!);
  const [choices, setChoices] = useState<string[]>(() =>
    initialAvatar && !HUMAN_CHARACTER_KEYS.includes(initialAvatar)
      ? [initialAvatar, ...HUMAN_CHARACTER_KEYS.slice(0, 7)]
      : [...HUMAN_CHARACTER_KEYS],
  );
  const [adult, setAdult] = useState(false);

  // A first connection that has not landed yet is worth waiting on quietly.
  // One that has already failed and is backing off is not.
  const unreachable = connection === 'reconnecting' || connection === 'offline';
  const waiting = pending || connection === 'connecting';

  // "Continue with Google" renders only when the server says it is configured;
  // a button that cannot work is worse than no button.
  const googleRef = useRef<HTMLDivElement | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void authApi.config().then(({ googleClientId }) => {
      if (cancelled || !googleClientId || !googleRef.current) return;
      mountGoogleButton(googleRef.current, googleClientId, (credential) => {
        void submitCredential(() => authApi.google(credential));
      })
        .then(() => setGoogleReady(true))
        .catch(() => setGoogleReady(false));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitCredential(call: () => ReturnType<typeof authApi.login>) {
    setBusy(true);
    setError(null);
    const result = await call();
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onSignedIn(result.fresh);
    if (result.fresh) {
      setHandle(result.identity.handle === 'Guest' ? '' : result.identity.handle);
      if (result.identity.avatar) setAvatar(result.identity.avatar);
      setStep('profile');
    }
  }

  const shuffle = () => {
    const fresh = randomAvatar();
    setChoices((prev) => [fresh, ...prev].slice(0, 8));
    setAvatar(fresh);
  };

  return (
    <main className="auth">
      <div className="auth__hero">
        <img className="auth__hero-logo" src="/logo.svg" alt="" />
        <span className="auth__hero-name">{t('app.name')}</span>
        <p className="auth__hero-tag">{t('app.tagline')}</p>
      </div>

      {step === 'welcome' ? (
        <form
          className="panel auth__card"
          onSubmit={(e) => {
            e.preventDefault();
            void submitCredential(() =>
              mode === 'signup' ? authApi.register(email, password) : authApi.login(email, password),
            );
          }}
        >
          <h1>{t('auth.heading')}</h1>

          <div className="auth__providers">
            <div ref={googleRef} style={googleReady ? undefined : { display: 'none' }} />
          </div>
          {googleReady && <div className="auth__divider">{t('auth.or')}</div>}

          <label className="field">
            <span className="field__label">{t('auth.email.label')}</span>
            <input
              className="field__input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>
          <label className="field">
            <span className="field__label">{t('auth.password.label')}</span>
            <input
              className="field__input"
              type="password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>

          {error && (
            <p className="auth__error" role="alert">
              {t(`auth.error.${error}`)}
            </p>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={busy} aria-busy={busy}>
            {mode === 'signup' ? t('auth.signup') : t('auth.signin')}
          </button>

          <p className="auth__switch">
            {mode === 'signup' ? t('auth.haveAccount') : t('auth.newHere')}{' '}
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'signup' ? 'signin' : 'signup');
                setError(null);
              }}
            >
              {mode === 'signup' ? t('auth.signin') : t('auth.signup')}
            </button>
          </p>

          <div className="auth__divider">{t('auth.or')}</div>

          <button
            type="button"
            className="btn btn--block"
            onClick={() => setStep('profile')}
            disabled={busy}
          >
            {t('auth.guest')}
          </button>
        </form>
      ) : (
        <div className="panel auth__card">
          <button type="button" className="btn btn--ghost btn--small auth__back" onClick={() => setStep('welcome')}>
            ‹ {t('declare.cancel')}
          </button>
          <h1>{t('auth.profile.heading')}</h1>

          <label className="field">
            <span className="field__label">{t('auth.name.label')}</span>
            <input
              className="field__input"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder={t('auth.name.placeholder')}
              maxLength={24}
              autoFocus
            />
          </label>

          {/* Choosing a face is the cheapest emotional hook a social product
              has, so it is front and centre — with a shuffle, because the
              space of faces is much bigger than one grid. */}
          <fieldset className="avatar-picker">
            <div className="avatar-picker__head">
              <legend className="field__label">{t('auth.pickCharacter')}</legend>
              <button type="button" className="btn btn--small" onClick={shuffle}>
                🎲 {t('auth.shuffle')}
              </button>
            </div>
            <div className="avatar-picker__grid">
              {choices.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`avatar-picker__item${avatar === option ? ' is-selected' : ''}`}
                  onClick={() => setAvatar(option)}
                  aria-pressed={avatar === option}
                  aria-label={option}
                >
                  <Portrait character={option} handle={handle || '·'} size={64} />
                </button>
              ))}
            </div>
          </fieldset>

          {/*
            §2.2 — 18+ only, self-declared, enforced at account level. This avoids
            building age assurance and avoids the children's-design-code surface
            entirely. The reasoning is written down because it will be asked about.
          */}
          <label className="check">
            <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} />
            <span>{t('auth.adult')}</span>
          </label>
          <p className="auth__note">{t('auth.adult.note')}</p>

          {/*
            The action stays available while the socket opens — the request is
            queued and sent on connect — but it says so, because a button that
            appears to do nothing is worse than one that admits it is waiting.
          */}
          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={!adult || pending}
            aria-busy={waiting}
            onClick={() => {
              if (!adult) return;
              onSubmit(handle.trim() || 'Guest', avatar);
            }}
          >
            {waiting ? t('auth.guest.connecting') : t('auth.enter')}
          </button>

          {/*
            "Connecting…" forever is a lie once the socket has actually failed,
            and it is the reason an unreachable server reads as a hung app. Say
            what happened, and offer the one action that helps.
          */}
          {unreachable && (
            <p className="auth__unreachable" role="status">
              <span>{t('error.offline')}</span>
              <button type="button" className="btn btn--small" onClick={onRetry}>
                {t('auth.retry')}
              </button>
            </p>
          )}
        </div>
      )}
    </main>
  );
}
